const theme = {
  space: [0, 4, 8, 12, 16, 24, 32, 48, 64],
  breakpoints: ['834px', '1050px'],
  colors: {
    primary: '#ffc436',
    darkyellow: '#fbb919',
    whitesmoke: '#f2f2f2',
    oldlace: 'rgba(241, 237, 219, 0.5)', // zebra, input & fieldset bg
    background: '#fffeef',
    white: '#ffffff',
    black: '#292827',
    text: '#26344A',
    textPrimary: '#f3ad03',
    green: '#c3dea8',
    textGreen: '#3f7537',
    gray: '#8d8d8d',
    lightGray: '#717782',
    success: '#78cdbe',
    warning: '#f87e0e',
    inactive: '#BBC9D1',
    progress: '#d0a755',
    reactivated: '#d0a755',
    deactivated: '#c79898',
    expired: '#c79898',
    disabled: '#cecece',
    incomplete: '#5493e8',
    completed: '#5493e8',
    waiting: '#fab3b1',
    invited: '#b3ceee',
    review: '#bbc9d1',
    accent: 'blue',
    muted: '#f7f7f7',
    highlight: '#f7f7f7',
    error: '#F50C0C',
    helpCenterBlue: '#5776A8',
    blue_5: '#d3e5f8',
    blue_6: '#1D66AF',
  },
  fonts: {
    body: '"Inter", system-ui, sans-serif',
    heading: '"Inter", system-ui, sans-serif',
  },
  fontSizes: [12, 14, 16, 20, 22, 28],
  fontWeights: {
    light: 300,
    body: 400,
    heading: 600,
    bold: 600,
  },
  lineHeights: {
    body: 1.5,
    heading: 1.1875,
  },
  layout: {
    container: {
      width: '100%',
      maxWidth: 1116,
      mx: 'auto',
    },
    labelWithRadio: {
      display: 'flex',
      fontSize: 2,
      py: 11,
      fontWeight: 'body',
      alignItems: 'center',
    },
    labelWithCheckbox: {
      fontSize: 2,
      pt: 11,
      fontWeight: 'body',
      mx: '-7.5px',
      '& > *': {
        mx: '7.5px',
      },
    },
    resultsTray: {
      container: {
        position: 'absolute',
        top: '100%',
        mt: '4px',
        width: 'calc(100% - 1px)',
        zIndex: 1,
      },
      default: {
        display: 'flex',
        p: 0,
        overflow: 'auto',
        transition: '400ms ease-in-out',
        willChange: 'opacity, transform',
        borderRadius: 20,
        transformOrigin: '50% 0',
        boxShadow: '0px 6px 25px 0px rgba(0, 0, 0, 0.1)',
        flexDirection: 'column',
        bg: 'white',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          my: 10,
        },
        '&::-webkit-scrollbar-thumb': {
          bg: '#c4c4c4',
          borderRadius: 10,
          mr: 100,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#717782',
        },
      },
      search: {
        variant: 'layout.resultsTray.default',
        border: '0.5px solid #717782',
        maxHeight: 275,
      },
      select: {
        variant: 'layout.resultsTray.default',
        maxHeight: 272,
      },
    },
    result: {
      search: {
        ':nth-of-type(even)': {
          bg: '#F2F6FC',
        },
        p: 3,
        py: 4,
        justifyContent: 'start',
        fontSize: 1,
        cursor: 'pointer',
        flexWrap: 'wrap',
      },
      select: {},
      locationSelect: {
        pl: 20,
        pb: 30,
        '&:first-of-type': {
          pt: 30,
        },
      },
      locationSearch: {
        variant: 'layout.result.search',
        '&:hover': {
          fontSize: 2,
          fontWeight: 600,
          color: '#26344A',
          '& *': {
            fontSize: 2,
            fontWeight: 600,
            color: '#26344A',
          },
        },
      },
      userSearch: {
        variant: 'layout.result.search',
        justifyContent: 'space-between',
      },
    },
    footer: {
      minHeight: 60,
      p: 4,
      justifyContent: 'center',
      alignItems: 'center',
      bg: 'background',
    },
  },
  userProfile: {
    basic: {
      m: 'auto',
      flexDirection: 'column',
      alignContent: 'center',
    },
    form: {
      variant: 'userProfile.basic',
      maxWidth: [335, 770],
    },
    student_list: {
      variant: 'userProfile.basic',
      maxWidth: 1300,
      width: '99%',
    },
    training_details: {
      variant: 'userProfile.basic',
      maxWidth: [335, 770],
    },
    reflection_questions: {
      variant: 'userProfile.basic',
      maxWidth: [335, 770],
    },
  },
  forms: {
    locationForm: {
      mt: 15,
      m: ['auto', 'unset'],
      flexDirection: 'column',
      alignContent: 'center',
      maxWidth: 335,
    },
    h1: {
      fontSize: 32,
    },
    h2: {
      fontSize: 18,
    },

    section: {
      default: {
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        mt: 20,
      },
      header: {
        flex: ['1 0 100%'],
        py: 20,
      },
      view: {
        variant: 'forms.section.default',
        justifyContent: 'flex-start',
        '& > *': {
          variant: 'forms.inputGroup.view',
        },
      },
      edit: {
        variant: 'forms.section.default',
        '& > *': {
          variant: 'forms.inputGroup.edit',
        },
      },
    },
    inputGroup: {
      view: {
        flex: '0 0 280px',
        wordWrap: 'break-word',
      },
      edit: {
        flex: ['0 1 100%', '0 0 calc(50% - 50px)'],
      },
      fullWidth: {
        flex: '1 0 100%',
      },
      radio: {
        display: 'flex',
        flexDirection: 'column',
      },
    },
    input: {
      p: 4,
      bg: 'oldlace',
      border: 0,
      fontFamily: 'body',
      fontSize: [1, 2],
      borderRadius: 8,
      height: 50,
      transition: 'opacity 400ms ease-in-out',
      '&:focus': {
        borderColor: 'primary',
        boxShadow: (t: { colors: { primary: string } }) =>
          `0 0 0 2px ${t.colors.primary}`,
        outline: 'none',
      },
      '&:disabled': {
        cursor: 'default',
        pointerEvents: 'none',
        opacity: 0.6,
      },
      '&::placeholder': {
        color: 'lightGray',
      },
    },
    timeInput: {
      variant: 'forms.input',
      cursor: 'text',
      '&::-webkit-inner-spin-button': {
        display: 'none',
      },
      '&::-webkit-clear-button': {
        display: 'none',
      },
      '&::-webkit-calendar-picker-indicator': {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        width: 'auto',
        height: 'auto',
        opacity: 0,
        cursor: 'pointer',
      },
    },
    search: {
      variant: 'forms.input',
      position: 'relative',
      color: 'text',
      bg: 'white',
      borderRadius: 40,
      border: (t: { colors: { lightGray: string } }) => [
        `0.5px solid ${t.colors.lightGray}`,
        'unset',
      ],
      '&:focus': {
        border: (t: { colors: { lightGray: string; primary: string } }) => [
          `0.5px solid ${t.colors.lightGray}`,
          `0.5px solid ${t.colors.primary}`,
        ],
        outline: 'none',
      },
    },
    genericSearch: {
      variant: 'forms.input',
      position: 'relative',
      color: 'text',
      bg: 'white',
      border: '0.5px solid #717782',
      height: 50,
      transition: '300ms ease-in-out',
      borderRadius: 40,
      '&:focus': {
        border: '0.5px solid #717782',
        outline: 'none',
      },
    },
    label: {
      pb: 2,
      color: 'lightGray',
      fontSize: 1,
      fontWeight: 'bold',
      userSelect: 'none',
    },
    radio: {
      width: 22,
      height: 22,
      color: 'primary',
      '&:focus svg': {
        color: 'red',
      },
      cursor: 'pointer',
    },
    checkbox: {
      m: 0,
      bg: 'white',
      cursor: 'pointer',
    },
    filter: {
      p: 0,
      color: 'text',
      userSelect: 'none',
      cursor: 'pointer',
    },
    buttons: {
      container: {
        mt: '4px',
        mb: '4px',
        justifyContent: 'start',
        flexWrap: 'wrap',
        maxWidth: ['100%', 440],
      },
      primary: {
        variant: 'buttons.primary',
        flex: ['1 1 90%', '0 0 200px'],
        m: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 50,
      },
      secondary: {
        position: 'relative',
        px: [5, 7],
        py: [3, 4],
        fontFamily: 'body',
        fontSize: 1,
        color: 'text',
        fontWeight: 600,
        outline: 'none',
        cursor: 'pointer',
        bg: 'white',
        m: 1,
        display: 'flex',
        justifyContent: 'center',
        flex: ['1 1 auto', '0 0 100px'],
        borderColor: 'primary',
        height: 50,
        '&:disabled': {
          opacity: 0.8,
          pointerEvents: 'none',
          cursor: 'pointer',
        },
      },
    },
  },
  buttons: {
    primary: {
      position: 'relative',
      px: [5, 7],
      py: [3, 4],
      fontFamily: 'body',
      fontSize: 1,
      color: 'text',
      background: 'primary',
      fontWeight: 600,
      borderRadius: 100,
      outline: 'none',
      transition: '300ms ease-in-out',
      boxShadow: '0px 5px 4px rgba(0, 0, 0, 0.03)',
      cursor: 'pointer',
      '&:before': {
        content: '" "',
        position: 'absolute',
        top: '0px',
        left: '0px',
        right: '0px',
        bottom: '0px',
        boxShadow: '0px 7px 6px rgba(0, 0, 0, 0.05)',
        opacity: 0,
        willChange: 'opacity',
        transition: 'opacity 300ms',
        borderRadius: 100,
      },
      '&:hover:before': {
        opacity: 1,
      },
      '&:disabled': {
        opacity: 0.8,
        pointerEvents: 'none',
        cursor: 'pointer',
      },
    },
    white: {
      variant: 'buttons.primary',
      color: 'text',
      background: 'white',
      transition: 'background 300ms ease-in-out',
      '&:hover': {
        backgroundColor: '#f1f2f3',
      },
      '&:disabled': {
        opacity: 0.8,
        pointerEvents: 'none',
        cursor: 'pointer',
      },
    },
    outline: {
      variant: 'buttons.primary',
      bg: 'inherit',
      color: 'text',
      background: 'white',
      borderWidth: '2px',
      borderStyle: 'solid',
      colorColor: 'text',
      '&:disabled': {
        color: '#b1b7bf',
        borderColor: 'currentColor',
        pointerEvents: 'none',
      },
    },
    outlineWhite: {
      variant: 'buttons.primary',
      bg: 'inherit',
      color: 'text',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'white',
      '&:disabled': {
        color: '#b1b7bf',
        borderColor: 'currentColor',
        pointerEvents: 'none',
      },
    },
    outlinePrimary: {
      variant: 'buttons.primary',
      bg: 'inherit',
      color: 'text',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'primary',
      '&:disabled': {
        color: '#b1b7bf',
        borderColor: 'currentColor',
        pointerEvents: 'none',
      },
    },
    pagination: {
      ml: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bg: 'muted',
      color: 'text',
      width: 35,
      height: 35,
      borderRadius: '50%',
      fontSize: 0,
      fontWeight: 600,
      outline: 'none',
      cursor: 'pointer',
      '&:disabled': {
        opacity: 0.4,
      },
    },
    icon: {
      default: {
        outline: 'none',
      },
      primary: {
        color: 'primary',
      },
      unlocked: {
        color: 'success',
      },
      locked: {
        color: 'warning',
      },
      progress: {
        color: 'progress',
      },
      'completed-review': {
        color: 'progress',
      },
      inactive: {
        color: 'inactive',
      },
      deactivated: {
        color: 'deactivated',
      },
      disabled: {
        color: 'disabled',
      },
      review: {
        color: 'review',
      },
      completed: {
        color: 'completed',
      },
      reactivated: {
        color: 'reactivated',
      },
      incomplete: {
        color: 'incomplete',
      },
      waiting: {
        color: 'waiting',
      },
      invited: {
        color: 'invited',
      },
    },
    close: {
      outline: 'none',
      cursor: 'pointer',
    },
  },
  tabbedMenu: {
    buttons: {
      selected: {
        variant: 'buttons.primary',
        boxShadow: 'none',
        fontSize: 2,
        transition: 'ease-in-out 0.3s',
        py: [2, 3],
        px: [5, 7],
        '&:hover:before': { opacity: 0 },
      },
      unselected: {
        variant: 'buttons.primary',
        boxShadow: 'none',
        fontSize: 2,
        transition: 'ease-in-out 0.3s',
        py: [2, 3],
        px: [5, 7],
        background: '#FDF0D1',
        '&:hover:before': { opacity: 0 },
      },
    },
    questionText: {
      variant: 'text.default',
      fontSize: 2,
    },
    questionAnswer: {
      variant: 'text.strong',
      fontWeight: 'heading',
      fontSize: 2,
      lineHeight: '20px',
      letterSpacing: '-0.01em',
      textAlign: 'left',
    },
    h3: {
      fontSize: 22,
    },
  },
  text: {
    default: {
      color: 'text',
      fontSize: 1,
      fontFamily: 'body',
      fontWeight: 'body',
    },
    basic: {
      color: 'text',
      fontSize: 2,
      fontFamily: 'body',
      fontWeight: 'body',
    },
    informational: {
      variant: 'text.default',
      fontSize: 1,
      color: 'lightGray',
    },
    secondary: {
      fontSize: 2,
      color: 'gray',
      fontFamily: 'body',
      fontWeight: 'body',
    },
    caps: {
      textTransform: 'uppercase',
      letterSpacing: '0.075em',
      fontSize: 0,
      fontFamily: 'body',
    },
    strong: {
      fontWeight: 'heading',
      fontFamily: 'body',
      fontSize: 1,
    },
    heading: {
      fontSize: 5,
      fontWeight: 'heading',
      fontFamily: 'heading',
      color: 'text',
    },
    h1: {
      variant: 'text.heading',
      fontSize: 5,
    },
    h2: {
      variant: 'text.heading',
      fontSize: 4,
      mb: 3,
    },
    h3: {
      variant: 'text.heading',
      fontWeight: 600,
      fontSize: 18,
      lineHeight: '22px',
    },
    logo: {
      variant: 'text.heading',
      fontSize: 20,
      cursor: 'pointer',
    },
    warning: {
      variant: 'text.body',
      color: 'warning',
    },
    error: {
      variant: 'text.body',
      fontWeight: 'heading',
      color: 'error',
    },
    results: {
      variant: 'text.body',
      fontSize: 0,
    },
    nsResults: {
      variant: 'text.results',
      fontSize: 16,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    accent: {
      color: 'textPrimary',
      fontSize: 2,
      fontWeight: 600,
    },
  },
  links: {
    default: {
      lineHeight: 1.142,
      textDecoration: 'none',
      '& > span': {
        borderBottom: 'solid 1px rgba(0, 0, 0, 0.9)',
      },
    },

    nav: {
      color: (t: { colors: { text: string } }) => t.colors.text,
      variant: 'links.default',
      display: 'block',
      position: 'relative',
      my: 30,
      pb: 1,
      fontSize: 2,
      fontWeight: 'body',
      fontFamily: 'body',
      borderBottom: 'solid 0px rgba(0,0,0,0.9)',
      '&:first-of-type': {
        borderRight: 0,
      },
      '&:last-of-type': {
        mb: 0,
        borderRight: 0,
      },
      '&:active': {
        color: 'black',
      },
      '&:focus': {
        color: 'black',
      },
      '&:visited': {
        color: 'text',
      },
      '&.div': {
        cursor: 'pointer',
      },
    },
    user: {
      variant: 'links.nav',
      textDecoration: 'none',
      fontSize: 0,
      fontWeight: 'bold',
      px: 4,
      '&:hover': {
        color: 'black',
      },
    },
    footer: {
      variant: 'links.default',
      fontSize: 10,
      color: 'lightGray',
    },
    router: {
      variant: 'links.default',
      color: 'text',
      textAlign: 'center',
      fontWeight: 'heading',
      fontSize: 1,
    },
    article: {
      container: {
        basic: {
          pl: 3,
          py: 4,
          display: 'block',
          width: '100%',
        },
        search: {
          p: 2,
          pl: 2,
          pt: 4,
          display: 'block',
          width: '100%',
        },
        list: {},
      },
      title: {
        default: {
          color: 'helpCenterBlue',
          width: '100%',
          fontSize: 18,
          transition: '100ms',
        },
        basic: {
          variant: 'links.article.title.default',
          display: 'inline',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'helpCenterBlue',
          '&:hover': {
            color: '#292827',
            borderBottomColor: '#292827',
          },
        },
        search: {
          variant: 'links.article.title.basic',
        },
        list: {
          variant: 'links.article.title.default',
          fontSize: 16,
          display: 'block',
          p: 3,
          px: 2,
          '&:hover': {
            bg: 'rgba(0,0,0,.10)',
          },
        },
      },
      preview: {
        fontSize: 1,
        color: '#585858',
        lineHeight: '1.55em',
      },
    },
  },
  cards: {
    white: {
      backgroundColor: 'white',
      color: 'text',
      borderRadius: '8px',
      variant: 'text.default',
    },
    yellow: {
      backgroundColor: 'primary',
      color: 'text',
      borderRadius: '8px',
      variant: 'text.default',
    },
    fieldset: {
      backgroundColor: 'oldlace',
      color: 'text',
      borderRadius: '8px',
      variant: 'text.default',
    },
    cursor: 'pointer',
  },
  badges: {
    default: {
      bg: 'white',
      fontSize: 0,
      px: 3,
      py: 1,
      borderRadius: '4px',
      textTransform: 'capitalize',
    },
    unlocked: {
      variant: 'badges.default',
      bg: 'success',
    },
    'signature-pending': {
      variant: 'badges.default',
      bg: 'success',
    },
    progress: {
      variant: 'badges.default',
      bg: 'progress',
    },
    'completed-review': {
      variant: 'badges.default',
      bg: 'progress',
    },
    locked: {
      variant: 'badges.default',
      bg: 'warning',
    },
    'pre-course': {
      variant: 'badges.default',
      bg: 'warning',
    },
    review: {
      variant: 'badges.default',
      bg: 'review',
    },
    inactive: {
      variant: 'badges.default',
      bg: 'inactive',
    },
    deactivated: {
      variant: 'badges.default',
      bg: 'deactivated',
    },
    expired: {
      variant: 'badges.default',
      bg: 'expired',
    },
    disabled: {
      variant: 'badges.default',
      bg: 'disabled',
    },
    completed: {
      variant: 'badges.default',
      bg: 'completed',
    },
    completedTmatt: {
      variant: 'badges.default',
      bg: 'completed',
    },
    reactivated: {
      variant: 'badges.default',
      bg: 'reactivated',
    },
    incomplete: {
      variant: 'badges.default',
      bg: 'incomplete',
    },
    'no-app-access': {
      variant: 'badges.default',
      bg: 'deactivated',
    },
    'app-account-deleted': {
      variant: 'badges.default',
      bg: 'error',
    },
    waiting: {
      variant: 'badges.default',
      bg: 'waiting',
    },
    invited: {
      variant: 'badges.default',
      bg: 'invited',
    },
    TMLevel: {
      variant: 'badges.default',
      color: 'white',
      fontSize: 10,
      px: '7px',
      py: '4px',
      bg: 'gray',
    },
  },
  styles: {
    a: {
      textDecoration: 'none',
      color: 'text',
    },
    table: {
      p: 5,
      width: '100%',
      borderCollapse: 'collapse',
      th: {
        textAlign: 'left',
        fontSize: 0,
        fontWeight: 'heading',
        p: 3,
      },
      td: {
        fontSize: 1,
        p: 3,
      },
      'td:nth-of-type(1)': {
        fontWeight: 'bold',
      },
      'td:nth-of-type(4)': {
        fontWeight: 'bold',
      },
      'tbody tr': {
        transition: '300ms ease-in-out',
        cursor: 'pointer',
      },
      'tbody tr:nth-of-type(odd)': {
        bg: 'rgba(241,237,219,0.3)',
      },
      'tbody tr:hover': {
        bg: 'rgba(241,237,219,0.66)',
        cursor: 'pointer',
      },
    },
  },
  messages: {
    error: {
      border: 0,
      padding: 0,
      background: 'none',
      color: 'warning',
    },
  },
  alerts: {
    primary: {
      color: 'white',
      bg: 'primary',
    },
    info: {
      color: 'text',
      bg: '#cdcde4',
    },
    notice: {
      color: 'text',
      bg: '#f87e0e',
    },
  },
}

export default theme
